import { useEffect, useRef, useState } from "react";
import { RefreshCw, X } from "lucide-react";

const MANIFEST_URL = "/asset-manifest.json";
const POLL_MS = 60_000;

async function fetchBuildId() {
  const res = await fetch(`${MANIFEST_URL}?t=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`manifest ${res.status}`);
  const data = await res.json();
  return data?.files?.["main.js"] || data?.entrypoints?.[0] || null;
}

export function VersionChecker() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const baseline = useRef(null);

  useEffect(() => {
    let active = true;

    const check = async () => {
      try {
        const current = await fetchBuildId();
        if (!active || !current) return;
        if (baseline.current == null) {
          baseline.current = current;
        } else if (current !== baseline.current) {
          setUpdateAvailable(true);
        }
      } catch (e) {
        // offline or dev server — ignore silently
      }
    };

    check();
    const id = setInterval(check, POLL_MS);
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      active = false;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  if (!updateAvailable || dismissed) return null;

  return (
    <div
      data-testid="version-update-banner"
      className="fixed inset-x-0 top-0 z-[100] flex items-center justify-center gap-3 bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-lg"
    >
      <RefreshCw className="h-4 w-4 shrink-0 animate-spin" style={{ animationDuration: "3s" }} />
      <span>A new version of WoltPe is available.</span>
      <button
        data-testid="version-refresh-button"
        onClick={() => window.location.reload()}
        className="rounded-full bg-primary-foreground/15 px-3 py-1 font-semibold transition-colors hover:bg-primary-foreground/25"
      >
        Refresh now
      </button>
      <button
        data-testid="version-dismiss-button"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        className="ml-1 rounded-full p-1 transition-colors hover:bg-primary-foreground/15"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export default VersionChecker;
